import styled, { keyframes, css } from 'styled-components'

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// Main Panel Container
export const QAPanelContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`

// Collapsible Trigger
export const QATrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: none;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const TriggerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

// Content Area
export const QAContentWrapper = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
`

export const QAContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.default};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`

// Messages Container
export const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
  max-height: 400px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin-bottom: ${({ theme }) => theme.spacing.default};

  @media (max-width: 640px) {
    max-height: min(400px, 50dvh);
    gap: ${({ theme }) => theme.spacing.small};
    margin-bottom: ${({ theme }) => theme.spacing.small};
  }
`

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const EmptyStateIcon = styled.div`
  font-size: 26px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  opacity: 0.5;
`

export const EmptyStateText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

// Message
export const MessageWrapper = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.2s ease-out;
`

export const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 90%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.5;
  white-space: pre-wrap;

  ${({ $role, theme }) =>
    $role === 'user'
      ? css`
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          border-bottom-right-radius: ${theme.borderRadius.small};
        `
      : css`
          background: ${theme.colors.background.secondary};
          color: ${theme.colors.text.primary};
          border-bottom-left-radius: ${theme.borderRadius.small};
        `}
`

// Citations Container
export const CitationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin-top: ${({ theme }) => theme.spacing.small};
`

// Citation Chip
export const CitationChip = styled.button<{
  $granularity: 'land_use_plan' | 'land_use_plan_overlay' | 'region'
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.micro}
    ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $granularity, theme }) => {
    if ($granularity === 'region') {
      return css`
        background: ${theme.colors.primaryLight};
        color: ${theme.colors.primary};
        &:hover {
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
        }
      `
    }
    return css`
      background: ${theme.colors.successLight};
      color: ${theme.colors.success};
      &:hover {
        background: ${theme.colors.success};
        color: ${theme.colors.text.inverse};
      }
    `
  }}
`

export const CitationIcon = styled.span`
  display: flex;
  align-items: center;
`

// Input Area
export const InputArea = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: flex-end;
  flex-shrink: 0;
`

export const TextInput = styled.textarea`
  flex: 1;
  min-height: 36px;
  max-height: 96px;
  padding: ${({ theme }) => theme.spacing.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  resize: none;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const MicButton = styled.button<{ $recording?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $recording }) =>
    $recording ? theme.colors.danger : theme.colors.background.tertiary};
  color: ${({ theme, $recording }) =>
    $recording ? theme.colors.text.inverse : theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme, $recording }) =>
      $recording ? theme.colors.danger : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const SendButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.background.tertiary : theme.colors.primary};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.text.tertiary : theme.colors.text.inverse};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not([disabled]) {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`

// Loading Indicator
export const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const LoadingSpinner = styled.div`
  width: 13px;
  height: 13px;
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

// Suggest Docs Button
export const SuggestDocsButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  margin-top: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
  }
`

// Error Message
export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: #fef2f2;
  color: ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`
